import * as cometdLib from 'cometd';
import type { TCometdEventData, TEvent } from './cometd.types';
import { URLS } from './cometd.constants';

export type SubscriptionHandle = cometdLib.SubscriptionHandle;

class CometDSingleton {
    private static instance: CometDSingleton;
    private cometd: cometdLib.CometD;
    private handshakePromise: Promise<string | undefined> | null = null;

    private constructor() {
        this.cometd = new cometdLib.CometD();
    }

    public static getInstance(): CometDSingleton {
        if (!CometDSingleton.instance) {
            CometDSingleton.instance = new CometDSingleton();
        }
        return CometDSingleton.instance;
    }

    public init(cometdConfig: cometdLib.Configuration): Promise<string | undefined> {
        if (!this.isInitialised()) {
            this.cometd.configure(cometdConfig);
            this.handshakePromise = this.getHandshakePromise();
        }
        return this.handshakePromise ?? Promise.reject('Handshake not initialized');
    }

    public async subscribe(
        channel: string,
        callback: (e: TEvent) => void,
        subscribeProps: object
    ): Promise<cometdLib.SubscriptionHandle> {
        await this.initIfNeeded();
        return new Promise((resolve) => {
            const subscription = this.cometd.subscribe(
                channel,
                this.parseMessage(callback),
                subscribeProps,
                resolve
            );
            resolve(subscription);
        });
    }

    public async unsubscribe(subscription: cometdLib.SubscriptionHandle): Promise<void> {
        const unsubscribeProps = {};
        await this.initIfNeeded();
        return new Promise<void>((resolve) => {
            this.cometd.unsubscribe(subscription, unsubscribeProps, () => {
                resolve();
            });
        });
    }

    public resubscribe(
        subscription: cometdLib.SubscriptionHandle,
        subscribeProps?: object
    ): cometdLib.SubscriptionHandle {
        return this.cometd.resubscribe(subscription, subscribeProps);
    }

    public addListener(channel: string, callback: cometdLib.Callback): cometdLib.ListenerHandle {
        return this.cometd.addListener(channel, callback);
    }

    public removeListener(listener: cometdLib.ListenerHandle): void {
        this.cometd.removeListener(listener);
    }

    private isInitialised(): boolean {
        return this.handshakePromise !== null;
    }

    private getHandshakePromise(): Promise<string | undefined> {
        return new Promise((resolve, reject) => {
            this.cometd.handshake((handshake) => {
                if (handshake.successful) {
                    resolve(handshake.clientId);
                } else {
                    reject(handshake.clientId);
                }
            });
        });
    }

    private async initIfNeeded(): Promise<void> {
        if (!this.isInitialised()) {
            await this.init({ url: URLS.DEFAULT });
        }
    }

    private parseMessage(
        callback: (message: { data: TCometdEventData; channel: string }) => void
    ): cometdLib.Callback {
        return ({ data, channel }) => {
            if (typeof data === 'string') {
                callback({ data: JSON.parse(data), channel });
            } else {
                callback({ data, channel });
            }
        };
    }
}

// Export the singleton instance
const cometdInstance = CometDSingleton.getInstance();
export default cometdInstance;
export { CometDSingleton };
